'use client'

import { type AxiosError, type AxiosResponse } from 'axios'
import useSWRMutation, { type SWRMutationResponse } from 'swr/mutation'
import { Md5 } from 'ts-md5'

import { type LoginRequestV3 } from '@redteclab/api/clients/bully'

import { buildHomeOneApiAxiosInstance } from '../../../api'
import { type VALIDATION_ERROR_FRIENDLY_CAPTCHA_FAILED } from '../../../base-form-handling'
import {
  type PublicGlobalConfig,
  useGlobalConfigContext,
} from '../../../global-config'
import {
  gtmDataLayerPushLogin,
  gtmDataLayerPushRegistration,
} from '../../../gtm'
import { HTTP_HEADER_SKIP_FRIENDLY_CAPTCHA_VERIFICATION } from '../../../http'
import { urlResolverGetLoginApiEndpoint } from '../../../url-handling'
import {
  refetchUserApiSwrSessionV1Customer,
  userApiSwrRefetchMiniCart,
} from '../../../user-data'
import { type RegistrationType } from '../../consts'
import { userApiGetCustomerRegistrationType } from '../userApiGetCustomerRegistrationType'

import { userApiSwrGetCustomerPreferredLanguage } from './useApiSwrGetCustomerPreferredLanguage'

export type UserLoginResponseError = {
  error?: typeof VALIDATION_ERROR_FRIENDLY_CAPTCHA_FAILED
  message?: string
  status?: number
}

// GTM login error codes
const GTM_LOGIN_WITH_PASSWORD_RESPONSE_ERROR_CODE_WRONG_PASSWORD = 1
const GTM_LOGIN_WITH_PASSWORD_RESPONSE_ERROR_CODE_CUSTOMER_NOT_FOUND = 2
const GTM_LOGIN_WITH_PASSWORD_RESPONSE_ERROR_CODE_GENERAL = 3
const GTM_LOGIN_REGISTRATION_SUCCESS_CODE = 0

const getLoginGtmCode = (message?: string): number => {
  switch (message) {
    case 'customer-authentication.error.wrong.username.or.password': {
      return GTM_LOGIN_WITH_PASSWORD_RESPONSE_ERROR_CODE_WRONG_PASSWORD
    }
    case 'customer-data-service.error.bad_request': {
      return GTM_LOGIN_WITH_PASSWORD_RESPONSE_ERROR_CODE_CUSTOMER_NOT_FOUND
    }
    default: {
      return GTM_LOGIN_WITH_PASSWORD_RESPONSE_ERROR_CODE_GENERAL
    }
  }
}

const pushLoginWithPasswordGtmErrorEvent = (
  error: AxiosError<UserLoginResponseError>,
): void => {
  const data = error.response?.data
  let gtmLoginStatusCode = GTM_LOGIN_WITH_PASSWORD_RESPONSE_ERROR_CODE_GENERAL
  if (data && typeof data === 'object' && 'message' in data) {
    gtmLoginStatusCode = getLoginGtmCode(data.message)
  }

  gtmDataLayerPushLogin({
    gtmLoginStatusCode,
    loginActionType: 'loginWithPassword',
  })
}

const postSubmitLoginData =
  (globalConfig: PublicGlobalConfig) =>
  async (
    swrKey: string,
    data: { arg: LoginRequestV3 },
  ): Promise<AxiosResponse> => {
    return buildHomeOneApiAxiosInstance(globalConfig).post(
      urlResolverGetLoginApiEndpoint(),
      data.arg,
      {
        headers: {
          ...(process.env.NEXT_PUBLIC_SKIP_FRIENDLY_CAPTCHA === 'true' && {
            [HTTP_HEADER_SKIP_FRIENDLY_CAPTCHA_VERIFICATION]: 'true',
          }),
        },
      },
    )
  }

const getUserLoginMutationKey = (): string => {
  return 'useUserApiLoginMutation'
}

/**
 * after user logged in we refetch some swrs - e.g., minicart, or session to show
 * that the user is logged in on the account icon
 */

export const refetchCustomerBasicInformationWhenUserLogsIn = async (
  globalConfig: PublicGlobalConfig,
  loginActionType: 'loginWithOtp' | 'loginWithPassword' | 'registration',
  registrationType?: RegistrationType,
): Promise<void> => {
  let userIdHash: string | null = null
  let customerNumber: string | null = null
  let resolvedRegistrationType: RegistrationType | undefined = registrationType

  // eslint-disable-next-line unicorn/try-complexity -- don't reference, refactor if touched
  try {
    const [customer, customerRegistrationType] = await Promise.all([
      refetchUserApiSwrSessionV1Customer(),
      loginActionType === 'registration'
        ? Promise.resolve(undefined)
        : userApiGetCustomerRegistrationType(globalConfig).catch(
            () => undefined,
          ),
    ])
    userIdHash = customer?.customerNumber
      ? Md5.hashStr(customer.customerNumber)
      : null
    customerNumber = customer?.customerNumber ?? null
    resolvedRegistrationType =
      registrationType ?? customerRegistrationType?.registrationType
  } finally {
    if (
      loginActionType === 'loginWithPassword' ||
      loginActionType === 'loginWithOtp'
    ) {
      gtmDataLayerPushLogin({
        ...(customerNumber && { customerNumber }),
        gtmLoginStatusCode: GTM_LOGIN_REGISTRATION_SUCCESS_CODE,
        loginActionType,
        registrationType: resolvedRegistrationType,
        userIdHash,
      })
    } else {
      gtmDataLayerPushRegistration(
        GTM_LOGIN_REGISTRATION_SUCCESS_CODE,
        userIdHash,
        resolvedRegistrationType,
      )
    }
  }

  await userApiSwrRefetchMiniCart()
}

export const refetchSwrsWhenUserLogsIn = async (
  globalConfig: PublicGlobalConfig,
  loginActionType: 'loginWithOtp' | 'loginWithPassword',
): Promise<string | undefined> => {
  await refetchCustomerBasicInformationWhenUserLogsIn(
    globalConfig,
    loginActionType,
  )
  const language = await userApiSwrGetCustomerPreferredLanguage()

  return language?.preferredLanguage
}

export const useUserApiLoginMutation = ({
  onError,
  onSuccess,
}: {
  onError: () => void
  onSuccess?: (
    afterLoginSideEffectsPromise: Promise<string | undefined>,
  ) => void
}): SWRMutationResponse<
  AxiosResponse,
  AxiosError<UserLoginResponseError>,
  string,
  LoginRequestV3
> => {
  const globalConfig = useGlobalConfigContext()

  return useSWRMutation(
    getUserLoginMutationKey(),
    postSubmitLoginData(globalConfig),
    {
      onError: (error: AxiosError<UserLoginResponseError>) => {
        pushLoginWithPasswordGtmErrorEvent(error)
        onError()
      },
      onSuccess: () => {
        onSuccess?.(
          refetchSwrsWhenUserLogsIn(globalConfig, 'loginWithPassword'),
        )
      },
    },
  )
}
